import React, { useState, useRef } from 'react';
import { Box, IconButton, Tooltip, useTheme } from '@mui/material';
import { Celebration } from '@mui/icons-material';

/**
 * A reusable celebration button component with confetti animation
 *
 * @example
 * // Basic usage
 * <CelebrateButton />
 *
 * @example
 * // With custom properties
 * <CelebrateButton
 *   tooltipText="Woohoo!"
 *   confettiDuration={5000}
 *   confettiCount={200}
 *   sx={{ ml: 2 }}
 * />
 */
interface CelebrateButtonProps {
  /** Optional tooltip text. Defaults to "Celebrate" */
  tooltipText?: string;
  /** Duration in milliseconds for how long confetti shows. Defaults to 3000ms */
  confettiDuration?: number;
  /** Number of confetti pieces. Defaults to 150 */
  confettiCount?: number;
  /** Custom styling for the button */
  sx?: Record<string, any>;
}

const CelebrateButton: React.FC<CelebrateButtonProps> = ({
  tooltipText = 'Celebrate',
  confettiDuration = 3000,
  confettiCount = 150,
  sx = {},
}) => {
  const theme = useTheme();
  const celebrationButtonRef = useRef<HTMLButtonElement>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({ x: 0, y: 0 });
  const [confettiKeys] = useState(() => Array.from({ length: confettiCount }, (_, i) => `confetti-${i}`));

  const handleCelebrate = () => {
    // Get button position for confetti origin
    if (celebrationButtonRef.current) {
      const rect = celebrationButtonRef.current.getBoundingClientRect();
      setButtonPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      });
    }

    setShowConfetti(!showConfetti);
    if (!showConfetti) {
      // Auto-hide confetti after specified duration
      setTimeout(() => setShowConfetti(false), confettiDuration);
    }
  };

  return (
    <>
      {/* Confetti Overlay */}
      {showConfetti && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        >
          {confettiKeys.map((key, index) => {
            const randomAngle = Math.random() * Math.PI * 2;
            const randomRadius = Math.random() * 400 + 100;
            const endX = Math.cos(randomAngle) * randomRadius;
            const endY = Math.sin(randomAngle) * randomRadius + 400; // Add gravity
            const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff'];

            return (
              <div
                key={key}
                className="confetti-piece"
                style={
                  {
                    '--start-x': `${buttonPosition.x}px`,
                    '--start-y': `${buttonPosition.y}px`,
                    '--end-x': `${endX}px`,
                    '--end-y': `${endY}px`,
                    '--rotation-start': `${Math.random() * 360}deg`,
                    '--rotation-end': `${Math.random() * 1440 + 720}deg`,
                    '--delay': `${Math.random() * 0.2}s`,
                    '--bg-color': colors[Math.floor(Math.random() * colors.length)],
                  } as React.CSSProperties
                }
              />
            );
          })}
        </Box>
      )}

      {/* Embedded styles for confetti animation */}
      <style>
        {`
          .confetti-piece {
            position: absolute;
            top: var(--start-y);
            left: var(--start-x);
            width: 8px;
            height: 8px;
            background-color: var(--bg-color);
            border-radius: ${Math.random() > 0.5 ? '50%' : '0'};
            opacity: 0;
            animation: explode 3s ease-out forwards;
            animation-delay: var(--delay);
          }

          @keyframes explode {
            0% {
              transform: translate(0, 0) rotate(var(--rotation-start));
              opacity: 1;
            }
            100% {
              transform: translate(var(--end-x), var(--end-y)) rotate(var(--rotation-end));
              opacity: 0;
            }
          }
        `}
      </style>

      {/* The Celebrate Button */}
      <Tooltip title={tooltipText}>
        <IconButton
          ref={celebrationButtonRef}
          onClick={handleCelebrate}
          sx={{
            bgcolor: showConfetti ? theme.palette.primary.main : theme.palette.grey[200],
            color: showConfetti ? 'white' : theme.palette.primary.main,
            '&:hover': {
              bgcolor: showConfetti ? theme.palette.primary.dark : theme.palette.primary.light,
              color: 'white',
            },
            ...sx, // Allow custom styling to override defaults
          }}
        >
          <Celebration />
        </IconButton>
      </Tooltip>
    </>
  );
};

export default CelebrateButton;
